// components/ui/Button.tsx
// UI Phase 1 primitive: the one button. Five intents, two sizes, loading state.
// Warm student-product language: soft radius, fast transitions, visible focus.
// Intent semantics: danger = destructive/irreversible only; warning = caution
// (non-destructive but notable, e.g. a feedback submit). Keeping them distinct
// preserves red's meaning.

import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { cn } from './cn';
import { CONTROL_BASE, CONTROL_HEIGHT } from './control';
import type { ControlSize } from './control';

export type ButtonIntent = 'primary' | 'secondary' | 'ghost' | 'danger' | 'warning';
/** Button sizes ARE the control sizes — one height scale for the whole app. */
export type ButtonSize = ControlSize;

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  intent?: ButtonIntent;
  size?: ButtonSize;
  isLoading?: boolean;
  /** Optional icon rendered before the label */
  icon?: ReactNode;
}

const INTENT_CLASSES: Record<ButtonIntent, string> = {
  primary:
    'bg-primary-strong text-white hover:bg-primary-800 active:bg-primary-900 shadow-sm',
  secondary:
    'bg-surface-card text-ink border border-line-soft hover:bg-surface-sunken active:bg-surface-sunken shadow-sm',
  ghost:
    'bg-transparent text-ink-2 hover:bg-surface-sunken hover:text-ink active:bg-surface-sunken',
  danger:
    'bg-status-critical-strong text-white hover:bg-status-critical-strong/90 active:bg-status-critical-strong/80 shadow-sm',
  warning:
    'bg-status-warning-strong text-white hover:bg-status-warning-strong/90 active:bg-status-warning-strong/80 shadow-sm',
};

// Horizontal only. Height comes from CONTROL_HEIGHT — see control.ts for why
// padding must not be the thing that sets it. (These px/gap values reproduce
// the previous rendering exactly; only bordered `secondary` changes, losing the
// 2px its border used to add on top of the padding sum.)
const SIZE_CLASSES: Record<ButtonSize, string> = {
  sm: 'px-3 text-sm gap-1.5',
  md: 'px-4 text-sm gap-2',
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { intent = 'primary', size = 'md', isLoading = false, icon, className, children, disabled, ...rest },
    ref
  ) => (
    <button
      ref={ref}
      disabled={disabled || isLoading}
      className={cn(
        CONTROL_BASE,
        CONTROL_HEIGHT[size],
        'justify-center font-semibold',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        'disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none',
        INTENT_CLASSES[intent],
        SIZE_CLASSES[size],
        className
      )}
      {...rest}
    >
      {isLoading ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : icon}
      {children}
    </button>
  )
);

Button.displayName = 'Button';

export default Button;
